// Canonical Huffman decoder. Encoder lives in C++ Lambda — only the decode
// half is needed in the client.
//
// Wire format for `tableBytes`:
//   u32 LE  : symbolCount (number of distinct symbols)
//   u32 LE  : totalCount  (number of values in the stream)
//   then for each symbol:
//     u16 LE: symbol id
//     u8    : code length (0..MAX_CODE_LEN). Length 0 = unused (only for empty stream marker).
// Code values are derived canonically by the decoder from sorted (length, symbol).

export const MAX_SYMBOL = 0xffff;
export const MAX_CODE_LEN = 32;

interface SymbolEntry {
  symbol: number;
  length: number;
  code: number;
}

class BitReader {
  private idx = 0;
  private cur = 0;
  private nbits = 0;
  constructor(private readonly buf: Uint8Array) {}

  readBit(): number {
    if (this.nbits === 0) {
      if (this.idx >= this.buf.length) throw new Error("Huffman: unexpected end of stream");
      this.cur = this.buf[this.idx++]!;
      this.nbits = 8;
    }
    const bit = (this.cur >>> 7) & 1;
    this.cur = (this.cur << 1) & 0xff;
    this.nbits--;
    return bit;
  }
}

function readTable(tableBytes: Uint8Array): { entries: SymbolEntry[]; totalCount: number } {
  if (tableBytes.byteLength < 8) throw new Error("Huffman: table too small");
  const view = new DataView(tableBytes.buffer, tableBytes.byteOffset, tableBytes.byteLength);
  const n = view.getUint32(0, true);
  const totalCount = view.getUint32(4, true);
  const entries: SymbolEntry[] = [];
  for (let i = 0; i < n; i++) {
    const symbol = view.getUint16(8 + i * 3, true);
    const length = view.getUint8(8 + i * 3 + 2);
    entries.push({ symbol, length, code: 0 });
  }
  // Recompute canonical codes from (length, symbol).
  entries.sort((a, b) => (a.length !== b.length ? a.length - b.length : a.symbol - b.symbol));
  let code = 0;
  let prevLen = 0;
  for (const e of entries) {
    if (e.length === 0) continue;
    if (prevLen === 0) {
      code = 0;
    } else {
      code = (code + 1) << (e.length - prevLen);
    }
    e.code = code;
    prevLen = e.length;
  }
  return { entries, totalCount };
}

export function huffmanDecode(code: Uint8Array, tableBytes: Uint8Array, count: number): Int32Array {
  const { entries, totalCount } = readTable(tableBytes);
  if (count !== totalCount) {
    throw new Error(`Huffman count mismatch: requested ${count}, table=${totalCount}`);
  }
  const out = new Int32Array(count);
  if (count === 0) return out;
  if (entries.length === 1) {
    const sym = entries[0]!.symbol;
    out.fill(sym);
    return out;
  }

  // Length-grouped lookup: per-length array of (firstCode, firstIndex, size); linear search inside.
  const byLen: { length: number; firstCode: number; firstIndex: number; size: number }[] = [];
  const sortedByLen = [...entries].sort((a, b) =>
    a.length !== b.length ? a.length - b.length : a.code - b.code,
  );
  let i = 0;
  while (i < sortedByLen.length) {
    const len = sortedByLen[i]!.length;
    const firstCode = sortedByLen[i]!.code;
    const firstIndex = i;
    let j = i;
    while (j < sortedByLen.length && sortedByLen[j]!.length === len) j++;
    byLen.push({ length: len, firstCode, firstIndex, size: j - i });
    i = j;
  }

  const reader = new BitReader(code);
  for (let n = 0; n < count; n++) {
    let acc = 0;
    let len = 0;
    let matched = false;
    while (!matched) {
      acc = (acc << 1) | reader.readBit();
      len++;
      for (const grp of byLen) {
        if (grp.length !== len) continue;
        const offset = acc - grp.firstCode;
        if (offset >= 0 && offset < grp.size) {
          out[n] = sortedByLen[grp.firstIndex + offset]!.symbol;
          matched = true;
          break;
        }
      }
      if (len > MAX_CODE_LEN) throw new Error("Huffman: invalid code (exceeded max length)");
    }
  }
  return out;
}
